import {
  CoinVerticalIcon,
  StarIcon,
  HeartIcon,
  ClockClockwiseIcon,
} from "@phosphor-icons/react";
import { useIntl } from "react-intl";

import { SortButton } from "@/components/SortButton/SortButton";
import { ToggleButtonGroup } from "@/components/ToggleButtonGroup/ToggleButtonGroup";
import {
  HotelsSortOptions,
  type HotelsSortValue,
} from "@/features/hotels/types/types";
import { SortOrder } from "@/types/enums";

type Props = {
  value: HotelsSortValue;
  onChange: (newValue: HotelsSortValue) => void;
};

function HotelsListsSort({ value, onChange }: Props) {
  const intl = useIntl();

  return (
    <ToggleButtonGroup
      orientation="vertical"
      fullWidth
      exclusive
      size="small"
      color="primary"
      aria-label={intl.formatMessage({
        defaultMessage: "Sort options",
        description:
          "Accessible label for the sort options dropdown or selector",
      })}
    >
      <SortButton
        value={{ field: HotelsSortOptions.RECOMMENDED, order: SortOrder.ASC }}
        isSelected={
          value.field === HotelsSortOptions.RECOMMENDED &&
          value.order === SortOrder.ASC
        }
        onChange={onChange}
        selectionVariant="radio"
      >
        <HeartIcon size={20} className="fill-secondary" weight="bold" />
        {intl.formatMessage({
          defaultMessage: "Most Popular",
          description: "Sort option: recommended / most popular hotels first",
        })}
      </SortButton>

      <SortButton
        value={{ field: HotelsSortOptions.SPECIALDEAL, order: SortOrder.ASC }}
        isSelected={
          value.field === HotelsSortOptions.SPECIALDEAL &&
          value.order === SortOrder.ASC
        }
        onChange={onChange}
        selectionVariant="radio"
      >
        <ClockClockwiseIcon
          size={20}
          className="fill-secondary"
          weight="bold"
        />
        {intl.formatMessage({
          defaultMessage: "Exclusive offers",
          description: "Sort option: hotels with special deals first",
        })}
      </SortButton>

      <SortButton
        value={{ field: HotelsSortOptions.PRICE, order: SortOrder.ASC }}
        isSelected={
          value.field === HotelsSortOptions.PRICE &&
          value.order === SortOrder.ASC
        }
        onChange={onChange}
        selectionVariant="radio"
      >
        <CoinVerticalIcon size={20} className="fill-secondary" weight="bold" />
        {intl.formatMessage({
          defaultMessage: "Cheapest",
          description: "Sort option: cheapest hotels first",
        })}
      </SortButton>

      <SortButton
        value={{ field: HotelsSortOptions.RATING, order: SortOrder.DESC }}
        isSelected={
          value.field === HotelsSortOptions.RATING &&
          value.order === SortOrder.DESC
        }
        onChange={onChange}
        selectionVariant="radio"
      >
        <StarIcon size={20} className="fill-secondary" weight="bold" />
        {intl.formatMessage({
          defaultMessage: "Hotel Rating",
          description:
            "Sort option: highest-rated hotels first (by star rating and price)",
        })}
      </SortButton>
    </ToggleButtonGroup>
  );
}

export default HotelsListsSort;
